import React from 'react';
import { FormikHelpers } from 'formik';
import nl from 'date-fns/locale/nl';
import { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  FormDatePicker as DatePicker,
  FormTextarea as Textarea,
  FormTextField as TextField,
  useWindowDimensions,
} from '@uncinc/uncinc-react-kitchen-sink';

import { DEFAULT_LANGUAGE, SCREEN_NORMAL } from '../../../config/constants';
import formatDate from '../../../helpers/formatDate';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';
import Button from '../../Button';

import './style.scss';

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
registerLocale('nl', nl);

export type DairyEntryValues = {
  title: string
  body: string
  entryDate: string
};

type DairyFormHelpers = FormikHelpers<DairyEntryValues>;

type DiaryFormProps = {
  initialValues: DairyEntryValues
  onSubmit: (values: DairyEntryValues) => void
  onDelete?: () => void
};

const DiaryForm: React.FC<DiaryFormProps> = ({
  initialValues,
  onSubmit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();

  const { buildClass } = useClasses('DiaryForm');
  const operationStatusAdd = useOperationStatus('node', 'add');
  const operationStatusEdit = useOperationStatus('node', 'edit');

  // eslint-disable-next-line max-len
  const disableAddButton = operationStatusAdd === UI_OPERATION_STATUS_REQUEST || operationStatusEdit === UI_OPERATION_STATUS_REQUEST;

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .required(t('pages.diary.form.fields.title.validation.required')),
    body: Yup.string()
      .trim()
      .required(t('pages.diary.form.fields.body.validation.required')),
    entryDate: Yup.string()
      .nullable(true)
      .required(t('pages.diary.form.fields.entryDate.validation.required')),
  });

  return (
    <div className={buildClass()}>
      <FormikWrapper
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        noValidate
        onSubmit={(values: DairyEntryValues, { setSubmitting }: DairyFormHelpers) => {
          const formattedDate = formatDate(values.entryDate, 'YYYY-MM-DD');

          // We change the entryDate here from a Date object to a string (format YYYY-MM-DD),
          // so it's compliant with the format required from the backend.
          onSubmit({ ...values, entryDate: formattedDate });
          setSubmitting(false);
        }}
      >
        <TextField
          fieldName="title"
          title={t('pages.diary.form.fields.title.label')}
          placeholder={t('pages.diary.form.fields.title.placeholder')}
        />
        <Textarea
          fieldName="body"
          title={t('pages.diary.form.fields.body.label')}
          placeholder={t('pages.diary.form.fields.body.placeholder')}
        />
        <DatePicker
          fieldName="entryDate"
          title={t('pages.diary.form.fields.entryDate.label')}
          locale={DEFAULT_LANGUAGE}
          dateFormat="dd/MM/yyyy"
          withPortal={width < SCREEN_NORMAL}
          popperPlacement="bottom-end"
        />

        <div className={buildClass('buttons')}>
          <Button htmlType="submit" buttonState={disableAddButton && 'disabled'}>
            {t('pages.diary.form.submit')}
          </Button>

          {onDelete && (
            <Button
              htmlType="button"
              warning
              onClick={onDelete}
            >
              {t('pages.diary.edit.delete')}
            </Button>
          )}
        </div>
      </FormikWrapper>
    </div>
  );
};

export default DiaryForm;
